import type { PaymentStatusValue } from './paymentStatus';
import { apiRequest } from './apiClient';
import { formatExportFilenameDate, requestCsvExport } from './exportRequest';

/** Backend whitelist keys for POST /api/payments/export `columns`. */
export const PaymentExportColumn = {
    TRANS_ID: 'trans_id',
    BANK: 'bank',
    PAYEE_NAME: 'payee_name',
    REFERENCE: 'reference',
    FULLNAME: 'fullname',
    ACCOUNT_NAME: 'account_name',
    ACCOUNT_NUM: 'account_num',
    SSN: 'ssn',
    CONAME: 'coname',
    COSSN: 'cossn',
    AMOUNT: 'amount',
    PROCESS_DATE: 'process_date',
    CREATED_ON: 'created_on',
    TSTATUS: 'tstatus',
} as const;

export type PaymentExportColumnKey =
    (typeof PaymentExportColumn)[keyof typeof PaymentExportColumn];

/** Maps payments table customize-column keys to backend export column keys. */
const TABLE_COLUMN_TO_EXPORT_COLUMN: Record<string, PaymentExportColumnKey> = {
    transactionId: PaymentExportColumn.TRANS_ID,
    bank: PaymentExportColumn.BANK,
    trustedAccount: PaymentExportColumn.PAYEE_NAME,
    referenceNum: PaymentExportColumn.REFERENCE,
    debtorName: PaymentExportColumn.FULLNAME,
    accountName: PaymentExportColumn.ACCOUNT_NAME,
    accountNumber: PaymentExportColumn.ACCOUNT_NUM,
    ssn: PaymentExportColumn.SSN,
    coDebtorName: PaymentExportColumn.CONAME,
    coDebtorSsn: PaymentExportColumn.COSSN,
    amount: PaymentExportColumn.AMOUNT,
    processDate: PaymentExportColumn.PROCESS_DATE,
    status: PaymentExportColumn.TSTATUS,
};

export function mapTableColumnsToExportColumns(tableColumnKeys: string[]): PaymentExportColumnKey[] {
    return tableColumnKeys.reduce<PaymentExportColumnKey[]>((columns, key) => {
        const mapped = TABLE_COLUMN_TO_EXPORT_COLUMN[key];
        if (mapped && !columns.includes(mapped)) {
            columns.push(mapped);
        }
        return columns;
    }, []);
}
type PaymentBankOptionApi = {
    id: number;
    bank_name: string;
};

export type PaymentBankOption = {
    id: number;
    bankName: string;
};

type PaymentTrustedAccountOptionApi = {
    id: number;
    payee_name: string;
};

export type PaymentTrustedAccountOption = {
    id: number;
    payeeName: string;
};

export type ListPaymentsParams = {
    limit?: number;
    offset?: number;
    search?: string;
    /** PHASE1: Deposit bank IDs selected in the Payments checkbox filter. */
    bankIds?: number[];
    /** Trusted account (payee) IDs selected in the Payments checkbox filter. */
    payeeIds?: number[];
    /** Inclusive lower bound, `Y-m-d` (deposits: `vbat.created_on`; pending: `t.process_date`). */
    from?: string;
    /** Inclusive upper bound, `Y-m-d`. */
    to?: string;
    /** Omit to include every status (deposits + pending statuses per backend rules). */
    status?: PaymentStatusValue[];
    // PHASE2: bankingAccountIds?: number[];
    sort?:
        | 'process_date'
        | 'reference'
        | 'trans_id'
        | 'bank'
        | 'debtor_name'
        | 'co_debtor_name'
        | 'account_name'
        | 'trusted_account';
    order?: 'ASC' | 'DESC' | 'asc' | 'desc';
};

export type ExportPaymentsParams = Omit<ListPaymentsParams, 'limit' | 'offset'> & {
    columns: PaymentExportColumnKey[];
};

export type ExportPaymentsQueuedResult = {
    mode: 'queued';
    id: number;
    status: string;
    rowCountEstimate: number;
    message: string;
};

export type ExportPaymentsSyncResult = {
    mode: 'sync';
    filename: string;
};

export type ExportPaymentsResult = ExportPaymentsQueuedResult | ExportPaymentsSyncResult;

type PaymentListItemApi = {
    trans_id: number;
    bank: string;
    reference: string | null;
    fullname: string;
    account_name: string;
    account_num: string;
    ssn: string | null;
    coname: string;
    cossn: string;
    amount: number | null;
    process_date: string | null;
    tstatus: string | null;
    created_on: string | null;
    payee_id: number | null;
    payee_name: string;
    // PHASE2: banking_account_id?: number | null;
    // PHASE2: destination_bank_account?: string | null;
    // PHASE2: banking_account?: {
    // PHASE2:     id?: number | null;
    // PHASE2:     bank_name?: string | null;
    // PHASE2: } | null;
};

type PaymentsListApiResponse = {
    data: PaymentListItemApi[];
    pagination: {
        limit: number;
        offset: number;
    };
};

export type PaymentListItem = {
    transId: number;
    bank: string;
    reference: string | null;
    fullName: string;
    accountName: string;
    accountNumber: string;
    ssn: string | null;
    coName: string;
    coSsn: string;
    amount: number | null;
    processDate: string | null;
    tStatus: string | null;
    createdOn: string | null;
    payeeId: number | null;
    payeeName: string;
    // PHASE2: bankingAccountId: number | null;
    // PHASE2: destinationBankAccount: string;
};

export type PaymentsListResponse = {
    data: PaymentListItem[];
    pagination: {
        limit: number;
        offset: number;
    };
};

export type PaymentTotalsParams = Omit<ListPaymentsParams, 'limit' | 'offset' | 'sort' | 'order'>;

export type PaymentTotalsResponse = {
    totalAmount: number;
    total: number;
};

function mapPaymentRow(row: PaymentListItemApi): PaymentListItem {
    return {
        transId: row.trans_id,
        bank: row.bank,
        reference: row.reference,
        fullName: row.fullname,
        accountName: row.account_name ?? '',
        accountNumber: row.account_num ?? '',
        ssn: row.ssn,
        coName: row.coname,
        coSsn: row.cossn,
        amount: row.amount,
        processDate: row.process_date,
        tStatus: row.tstatus,
        createdOn: row.created_on,
        payeeId: row.payee_id,
        payeeName: row.payee_name ?? '',
        // PHASE2: bankingAccountId: row.banking_account_id ?? row.banking_account?.id ?? null,
        // PHASE2: destinationBankAccount:
        // PHASE2:     row.destination_bank_account
        // PHASE2:     ?? row.banking_account?.bank_name
        // PHASE2:     ?? '',
    };
}

function buildPaymentsQueryParams(params: ListPaymentsParams): Record<string, string | number | boolean | undefined> {
    const q: Record<string, string | number | boolean | undefined> = {};

    if (params.limit !== undefined) {
        q.limit = params.limit;
    }
    if (params.offset !== undefined) {
        q.offset = params.offset;
    }
    if (params.search !== undefined && params.search !== '') {
        q.search = params.search;
    }
    // PHASE1: Forward selected deposit-bank IDs to GET /api/payments.
    if (params.bankIds !== undefined && params.bankIds.length > 0) {
        Object.assign(q, { bank_ids: params.bankIds });
    }
    if (params.payeeIds !== undefined && params.payeeIds.length > 0) {
        Object.assign(q, { payee_ids: params.payeeIds });
    }
    if (params.from !== undefined && params.from !== '') {
        q.from = params.from;
    }
    if (params.to !== undefined && params.to !== '') {
        q.to = params.to;
    }
    if (params.status !== undefined && params.status.length > 0) {
        Object.assign(q, { status: params.status });
    }
    // PHASE2: if (params.bankingAccountIds !== undefined && params.bankingAccountIds.length > 0) {
    // PHASE2:     Object.assign(q, { banking_account_ids: params.bankingAccountIds });
    // PHASE2: }
    if (params.sort !== undefined) {
        q.sort = params.sort;
    }
    if (params.order !== undefined) {
        q.order = params.order;
    }

    return q;
}

function buildExportBody(params: ExportPaymentsParams): Record<string, unknown> {
    const body: Record<string, unknown> = {
        columns: params.columns,
    };

    if (params.search !== undefined && params.search !== '') {
        body.search = params.search;
    }
    if (params.bankIds !== undefined && params.bankIds.length > 0) {
        body.bank_ids = params.bankIds;
    }
    if (params.payeeIds !== undefined && params.payeeIds.length > 0) {
        body.payee_ids = params.payeeIds;
    }
    if (params.from !== undefined && params.from !== '') {
        body.from = params.from;
    }
    if (params.to !== undefined && params.to !== '') {
        body.to = params.to;
    }
    if (params.status !== undefined && params.status.length > 0) {
        body.status = params.status;
    }
    if (params.sort !== undefined) {
        body.sort = params.sort;
    }
    if (params.order !== undefined) {
        body.order = params.order;
    }

    return body;
}

/** Matches backend PaymentExportService::buildDownloadFilename(). */
export function buildPaymentsExportFilename(from?: string, to?: string): string {
    const fromBlank = !from || from.trim() === '';
    const toBlank = !to || to.trim() === '';
    if (fromBlank && toBlank) {
        return `FORTH_Creditor-Portal-Payments-${formatExportFilenameDate(undefined)}_all.csv`;
    }

    return `FORTH_Creditor-Portal-Payments-${formatExportFilenameDate(from)}-${formatExportFilenameDate(to)}.csv`;
}

/**
 * GET /api/payments — legacy deposits + pending payments for the authenticated creditor user.
 */
export const getPayments = async (params: ListPaymentsParams = {}): Promise<PaymentsListResponse> => {
    const response = await apiRequest<PaymentsListApiResponse>('/api/payments', 'GET', {
        params: buildPaymentsQueryParams(params),
    });

    return {
        data: response.data.map(mapPaymentRow),
        pagination: response.pagination,
    };
};

/**
 * GET /api/payments/totals — full-set count and amount for the same filters as GET /api/payments.
 */
export const getPaymentTotals = async (
    params: PaymentTotalsParams = {},
): Promise<PaymentTotalsResponse> => {
    const response = await apiRequest<{ total_amount: number; total: number }>('/api/payments/totals', 'GET', {
        params: buildPaymentsQueryParams(params),
    });

    return {
        totalAmount: response.total_amount,
        total: response.total,
    };
};

/**
 * POST /api/payments/export — sync CSV download or queued background export.
 */
export const exportPayments = async (params: ExportPaymentsParams): Promise<ExportPaymentsResult> => (
    requestCsvExport(
        '/api/payments/export',
        buildExportBody(params),
        buildPaymentsExportFilename(params.from, params.to),
    )
);

/**
 * GET /api/payments/banks - deposit-bank options for the Payments checkbox filter.
 */
export const getPaymentBankOptions = async (): Promise<PaymentBankOption[]> => {
    const response = await apiRequest<{ data: PaymentBankOptionApi[] }>('/api/payments/banks', 'GET');

    return response.data.map((option) => ({
        id: option.id,
        bankName: option.bank_name,
    }));
};

/**
 * GET /api/payments/trusted-accounts - trusted account (payee) options for the Payments checkbox filter.
 */
export const getPaymentTrustedAccountOptions = async (): Promise<PaymentTrustedAccountOption[]> => {
    const response = await apiRequest<{ data: PaymentTrustedAccountOptionApi[] }>(
        '/api/payments/trusted-accounts',
        'GET',
    );

    return response.data.map((option) => ({
        id: option.id,
        payeeName: option.payee_name,
    }));
};

/**
 * PATCH /api/payments/{payment_id}
 */
export const rejectPayment = async (
    paymentId: number,
    rejectReason: string,
    additionalNotes?: string,
): Promise<void> => {
    const body: { reject_reason: string; additional_notes?: string } = {
        reject_reason: rejectReason.trim(),
    };
    if (additionalNotes?.trim()) {
        body.additional_notes = additionalNotes.trim();
    }
    await apiRequest(`/api/payments/${paymentId}/reject`, 'PATCH', { body });
};
